import { useState } from "react";
import { Button, Modal, message } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, DataKitPublishDiffDto } from "../../../../domain/DataKitDto";
import "./DataKitPublishBar.scss";

interface Props {
  kit: DataKitDto;
  diff: DataKitPublishDiffDto;
  canWrite: boolean;
  onDone: () => void;
}

export function DataKitPublishBar({ kit, diff, canWrite, onDone }: Props) {
  const [busy, setBusy] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  if (diff.totalChanges === 0) return null;

  const count = diff.totalChanges;
  // A kit nobody is served by still stages its changes - an item with no published shadow row is
  // excluded from every venue resolution outright (DataKitAssignmentService.resolveItemsForVenue),
  // so publishing is what makes these items reachable at all the day a location is assigned. What
  // was wrong here was only the copy: promising "designs still show the last published values" and
  // offering "Publish to designs" when this kit reaches no design at all.
  const assigned = kit.locationCount > 0;

  const publish = () => {
    if (!kit.id) return;
    setBusy(true);
    DataKitApi.publishKit(kit.id)
      .then((res) => {
        const r = res.data;
        message.success(
          assigned
            ? `Published ${r.itemsPublished} item${r.itemsPublished === 1 ? "" : "s"} to every design using this kit`
            : `Published ${r.itemsPublished} item${r.itemsPublished === 1 ? "" : "s"}`
        );
        onDone();
      })
      .catch(() => message.error("Could not publish this kit"))
      .finally(() => setBusy(false));
  };

  const discard = () => {
    if (!kit.id) return;
    setBusy(true);
    DataKitApi.discardKit(kit.id)
      .then(() => {
        setConfirmDiscard(false);
        onDone();
      })
      .catch(() => message.error("Could not discard these changes"))
      .finally(() => setBusy(false));
  };

  return (
    <div className="data-kit-publish-bar">
      <WarningOutlined className="data-kit-publish-bar-icon" />
      <p className="data-kit-publish-bar-copy">
        <strong>
          {count} unpublished change{count === 1 ? "" : "s"}
        </strong>
        <span>
          {assigned
            ? " — designs still show the last published values."
            : " — this kit isn’t assigned to a location yet. Publishing now is what makes these items available to a design once it is."}
        </span>
      </p>
      {canWrite && (
        <>
          <Button
            type="primary"
            className="data-kit-publish-bar-btn"
            onClick={publish}
            loading={busy}
          >
            {assigned ? "Publish to designs" : "Publish"}
          </Button>
          <button
            type="button"
            className="data-kit-publish-bar-discard"
            onClick={() => setConfirmDiscard(true)}
            disabled={busy}
          >
            Discard
          </button>
        </>
      )}

      <Modal
        className={"clean one"}
        open={confirmDiscard}
        onCancel={() => setConfirmDiscard(false)}
      >
        <header>
          <h2>Discard unpublished changes?</h2>
        </header>
        <p className="data-kit-discard-copy">
          Every edit since the last publish goes back to its published value, and items created
          since then are deleted. Item availability is not affected — it never waits for a publish.
        </p>
        <footer>
          <Button size="large" onClick={() => setConfirmDiscard(false)}>
            Cancel
          </Button>
          <Button size="large" danger loading={busy} onClick={discard}>
            Discard
          </Button>
        </footer>
      </Modal>
    </div>
  );
}
